import React, { useState } from 'react';
import { X, Send, Check, Building2, Users, Fuel, AlertTriangle, Radar, Plane, Mail, Phone, User, MapPin, MessageSquare } from 'lucide-react';

const DemoRequestModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    // Informations de contact
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    position: '',
    airportName: '',
    country: '',
    
    // Modules d'intérêt
    modules: {
      fboManagement: true,
      fuelManagement: true,
      crm: true,
      emergencyAlerts: true,
      takeoffLandingTracking: true,
      hangarLeaseManagement: false,
      staffScheduling: false,
      vehicleFleetManagement: false,
      invoicingBilling: false,
      clientPortal: false,
      reportingAnalytics: false,
      maintenanceTracking: false
    },
    
    // Informations supplémentaires
    aircraftPerMonth: '',
    fuelGallonsPerMonth: '',
    numberOfHangars: '',
    numberOfEmployees: '',
    currentSoftware: '',
    message: '',
    preferredContactMethod: 'email'
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const modulesList = [
    { id: 'fboManagement', label: 'FBO Management', icon: Building2, description: 'Complete FBO operations management' },
    { id: 'fuelManagement', label: 'Fuel Management', icon: Fuel, description: 'Fuel stock, orders, pricing, invoicing' },
    { id: 'crm', label: 'CRM', icon: Users, description: 'Client relationship management' },
    { id: 'emergencyAlerts', label: 'Emergency Alerts', icon: AlertTriangle, description: 'Real-time emergency notifications' },
    { id: 'takeoffLandingTracking', label: 'Takeoff & Landing Tracking', icon: Radar, description: 'Automatic movement detection and billing' },
    { id: 'hangarLeaseManagement', label: 'Hangar & Lease Management', icon: Building2, description: 'Hangar rental and lease agreements' },
    { id: 'staffScheduling', label: 'Staff Scheduling', icon: Users, description: 'Employee shift planning' },
    { id: 'vehicleFleetManagement', label: 'Vehicle Fleet Management', icon: Plane, description: 'Ground vehicle tracking' },
    { id: 'invoicingBilling', label: 'Invoicing & Billing', icon: Check, description: 'Automated invoicing' },
    { id: 'clientPortal', label: 'Client Portal', icon: Users, description: 'Self-service client access' },
    { id: 'reportingAnalytics', label: 'Reporting & Analytics', icon: Check, description: 'Advanced reporting' },
    { id: 'maintenanceTracking', label: 'Maintenance Tracking', icon: Check, description: 'Aircraft maintenance scheduling' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        modules: { ...prev.modules, [name]: checked }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    // Get selected modules list
    const selectedModules = Object.keys(formData.modules).filter(key => formData.modules[key]);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

      const response = await fetch(`${API_URL}/demo-request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
         ...formData,
         selectedModules: selectedModules
      })
      });

      if (response.ok) {
        setSubmitted(true);
        setTimeout(() => {
          onClose();
          setSubmitted(false);
          // Reset form
          setFormData({
            firstName: '', lastName: '', email: '', phone: '', company: '', position: '',
            airportName: '', country: '',
            modules: {
              fboManagement: true, fuelManagement: true, crm: true, emergencyAlerts: true,
              takeoffLandingTracking: true, hangarLeaseManagement: false, staffScheduling: false,
              vehicleFleetManagement: false, invoicingBilling: false, clientPortal: false,
              reportingAnalytics: false, maintenanceTracking: false
            },
            aircraftPerMonth: '', fuelGallonsPerMonth: '', numberOfHangars: '',
            numberOfEmployees: '', currentSoftware: '', message: '', preferredContactMethod: 'email'
          });
        }, 2000);
      } else {
        const data = await response.json();
        setError(data.detail || 'Une erreur est survenue');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="demo-modal-overlay" onClick={onClose}>
      <div className="demo-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header with logo */}
        <div className="demo-modal-header">
          <div className="demo-modal-logo">
            <img src="/uploads/logos/logo_20260410_145221.svg" alt="aeroTrick Logo" className="demo-logo-img" />
            <div>
              <div className="demo-logo-name">aero<span>Trick</span></div>
              <span className="demo-logo-sub">FBO Management Solutions</span>
            </div>
          </div>
          <button className="demo-modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {submitted ? (
          <div className="demo-success">
            <div className="demo-success-icon">✓</div>
            <h3>Request Sent Successfully!</h3>
            <p>Our commercial team will contact you within 24 hours to schedule a personalized demo.</p>
            <button className="demo-btn-close" onClick={onClose}>Close</button>
          </div>
        ) : (
          <>
            <div className="demo-modal-body">
              <h2 className="demo-title">Request a Demo</h2>
              <p className="demo-subtitle">Tell us about your FBO needs and we'll tailor a demo just for you</p>

              <form onSubmit={handleSubmit}>
                {/* Contact Information */}
                <div className="demo-section">
                  <h3 className="demo-section-title">
                    <User size={18} />
                    Contact Information
                  </h3>
                  <div className="demo-row">
                    <div className="demo-field">
                      <label>First Name *</label>
                      <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required />
                    </div>
                    <div className="demo-field">
                      <label>Last Name *</label>
                      <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required />
                    </div>
                  </div>
                  <div className="demo-row">
                    <div className="demo-field">
                      <label>Email *</label>
                      <input type="email" name="email" value={formData.email} onChange={handleChange} required />
                    </div>
                    <div className="demo-field">
                      <label>Phone *</label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required />
                    </div>
                  </div>
                  <div className="demo-row">
                    <div className="demo-field">
                      <label>Company / FBO Name *</label>
                      <input type="text" name="company" value={formData.company} onChange={handleChange} required />
                    </div>
                    <div className="demo-field">
                      <label>Your Position</label>
                      <input type="text" name="position" value={formData.position} onChange={handleChange} />
                    </div>
                  </div>
                  <div className="demo-row">
                    <div className="demo-field">
                      <label>Airport Name *</label>
                      <input type="text" name="airportName" value={formData.airportName} onChange={handleChange} required />
                    </div>
                    <div className="demo-field">
                      <label>Country *</label>
                      <input type="text" name="country" value={formData.country} onChange={handleChange} required />
                    </div>
                  </div>
                </div>

                {/* Modules Selection */}
                <div className="demo-section">
                  <h3 className="demo-section-title">
                    <Plane size={18} />
                    Modules You're Interested In
                  </h3>
                  <div className="demo-modules-grid">
                    {modulesList.map(module => (
                      <label key={module.id} className="demo-module-checkbox">
                        <input
                          type="checkbox"
                          name={module.id}
                          checked={formData.modules[module.id]}
                          onChange={handleChange}
                        />
                        <div className="demo-module-content">
                          <module.icon size={16} />
                          <span>{module.label}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Operational Details */}
                <div className="demo-section">
                  <h3 className="demo-section-title">Operational Details</h3>
                  <div className="demo-row">
                    <div className="demo-field">
                      <label>Monthly Aircraft Movements</label>
                      <select name="aircraftPerMonth" value={formData.aircraftPerMonth} onChange={handleChange}>
                        <option value="">Select range</option>
                        <option value="0-50">0-50</option>
                        <option value="51-200">51-200</option>
                        <option value="201-500">201-500</option>
                        <option value="501-1000">501-1000</option>
                        <option value="1000+">1000+</option>
                      </select>
                    </div>
                    <div className="demo-field">
                      <label>Monthly Fuel (Gallons)</label>
                      <select name="fuelGallonsPerMonth" value={formData.fuelGallonsPerMonth} onChange={handleChange}>
                        <option value="">Select range</option>
                        <option value="0-5000">0-5,000</option>
                        <option value="5001-20000">5,001-20,000</option>
                        <option value="20001-50000">20,001-50,000</option>
                        <option value="50001-100000">50,001-100,000</option>
                        <option value="100000+">100,000+</option>
                      </select>
                    </div>
                  </div>
                  <div className="demo-row">
                    <div className="demo-field">
                      <label>Number of Hangars</label>
                      <select name="numberOfHangars" value={formData.numberOfHangars} onChange={handleChange}>
                        <option value="">Select</option>
                        <option value="0">0</option>
                        <option value="1-5">1-5</option>
                        <option value="6-20">6-20</option>
                        <option value="21-50">21-50</option>
                        <option value="50+">50+</option>
                      </select>
                    </div>
                    <div className="demo-field">
                      <label>Number of Employees</label>
                      <select name="numberOfEmployees" value={formData.numberOfEmployees} onChange={handleChange}>
                        <option value="">Select</option>
                        <option value="1-5">1-5</option>
                        <option value="6-20">6-20</option>
                        <option value="21-50">21-50</option>
                        <option value="51-100">51-100</option>
                        <option value="100+">100+</option>
                      </select>
                    </div>
                  </div>
                  <div className="demo-field">
                    <label>Current Management Software</label>
                    <input type="text" name="currentSoftware" value={formData.currentSoftware} onChange={handleChange} placeholder="e.g., None, QuickBooks, Ramco, etc." />
                  </div>
                </div>

                {/* Additional Info */}
                <div className="demo-section">
                  <h3 className="demo-section-title">
                    <MessageSquare size={18} />
                    Additional Information
                  </h3>
                  <div className="demo-field">
                    <label>Preferred Contact Method</label>
                    <div className="demo-radio-group">
                      <label><input type="radio" name="preferredContactMethod" value="email" checked={formData.preferredContactMethod === 'email'} onChange={handleChange} /> Email</label>
                      <label><input type="radio" name="preferredContactMethod" value="phone" checked={formData.preferredContactMethod === 'phone'} onChange={handleChange} /> Phone</label>
                      <label><input type="radio" name="preferredContactMethod" value="both" checked={formData.preferredContactMethod === 'both'} onChange={handleChange} /> Both</label>
                    </div>
                  </div>
                  <div className="demo-field">
                    <label>Additional Comments / Questions</label>
                    <textarea name="message" rows="3" value={formData.message} onChange={handleChange} placeholder="Tell us more about your specific needs..."></textarea>
                  </div>
                </div>

                {error && <div className="demo-error">{error}</div>}

                <button type="submit" className="demo-submit-btn" disabled={submitting}>
                  {submitting ? 'Sending...' : 'Request Demo'}
                  <Send size={16} />
                </button>
              </form>
            </div>
          </>
        )}
      </div>

      <style jsx>{`
        .demo-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.85);
          backdrop-filter: blur(8px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow-y: auto;
          padding: 20px;
        }
        
        .demo-modal {
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          border-radius: 24px;
          width: 100%;
          max-width: 800px;
          max-height: 90vh;
          overflow-y: auto;
          border: 1px solid rgba(59, 130, 246, 0.3);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
        }
        
        .demo-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid rgba(148, 163, 184, 0.1);
          background: rgba(8, 13, 26, 0.8);
          position: sticky;
          top: 0;
          z-index: 10;
        }
        
        .demo-modal-logo {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        
        .demo-logo-img {
          height: 48px;
          width: auto;
        }
        
        .demo-logo-name {
          font-size: 20px;
          font-weight: 700;
          color: white;
        }
        
        .demo-logo-name span {
          color: #3b82f6;
        }
        
        .demo-logo-sub {
          font-size: 10px;
          color: #94a3b8;
          letter-spacing: 1px;
          text-transform: uppercase;
        }
        
        .demo-modal-close {
          background: rgba(255, 255, 255, 0.1);
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #94a3b8;
          transition: all 0.2s;
        }
        
        .demo-modal-close:hover {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }
        
        .demo-modal-body {
          padding: 32px;
        }
        
        .demo-title {
          font-size: 28px;
          font-weight: 700;
          color: white;
          margin-bottom: 8px;
        }
        
        .demo-subtitle {
          color: #94a3b8;
          margin-bottom: 32px;
          font-size: 14px;
        }
        
        .demo-section {
          margin-bottom: 32px;
          padding-bottom: 24px;
          border-bottom: 1px solid rgba(148, 163, 184, 0.1);
        }
        
        .demo-section-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 16px;
          font-weight: 600;
          color: #60a5fa;
          margin-bottom: 20px;
        }
        
        .demo-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 16px;
        }
        
        .demo-field {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        
        .demo-field label {
          font-size: 12px;
          font-weight: 500;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .demo-field input,
        .demo-field select,
        .demo-field textarea {
          background: rgba(8, 13, 26, 0.8);
          border: 1px solid rgba(148, 163, 184, 0.2);
          border-radius: 10px;
          padding: 12px 14px;
          color: white;
          font-size: 14px;
          transition: all 0.2s;
        }
        
        .demo-field input:focus,
        .demo-field select:focus,
        .demo-field textarea:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        
        .demo-field input::placeholder,
        .demo-field textarea::placeholder {
          color: #475569;
        }
        
        .demo-modules-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 12px;
        }
        
        .demo-module-checkbox {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          background: rgba(8, 13, 26, 0.6);
          border: 1px solid rgba(148, 163, 184, 0.15);
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .demo-module-checkbox:hover {
          background: rgba(59, 130, 246, 0.1);
          border-color: rgba(59, 130, 246, 0.3);
        }
        
        .demo-module-checkbox input {
          width: 18px;
          height: 18px;
          cursor: pointer;
          accent-color: #3b82f6;
        }
        
        .demo-module-content {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: #cbd5e1;
        }
        
        .demo-radio-group {
          display: flex;
          gap: 20px;
        }
        
        .demo-radio-group label {
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          text-transform: none;
          font-size: 13px;
          color: #cbd5e1;
        }
        
        .demo-radio-group input {
          width: 16px;
          height: 16px;
          accent-color: #3b82f6;
        }
        
        .demo-submit-btn {
          width: 100%;
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          border: none;
          border-radius: 12px;
          padding: 14px;
          color: white;
          font-size: 16px;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          cursor: pointer;
          transition: all 0.2s;
          margin-top: 16px;
        }
        
        .demo-submit-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(59, 130, 246, 0.3);
        }
        
        .demo-submit-btn:disabled {
          opacity: 0.6;
          transform: none;
        }
        
        .demo-error {
          background: rgba(239, 68, 68, 0.15);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 10px;
          padding: 12px;
          color: #f87171;
          font-size: 13px;
          margin-bottom: 16px;
        }
        
        .demo-success {
          text-align: center;
          padding: 48px 32px;
        }
        
        .demo-success-icon {
          width: 64px;
          height: 64px;
          background: linear-gradient(135deg, #22c55e, #16a34a);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          color: white;
          margin: 0 auto 20px;
        }
        
        .demo-success h3 {
          font-size: 24px;
          color: white;
          margin-bottom: 12px;
        }
        
        .demo-success p {
          color: #94a3b8;
          margin-bottom: 24px;
        }
        
        .demo-btn-close {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(148, 163, 184, 0.2);
          border-radius: 10px;
          padding: 10px 24px;
          color: white;
          cursor: pointer;
        }
        
        @media (max-width: 640px) {
          .demo-row {
            grid-template-columns: 1fr;
          }
          .demo-modules-grid {
            grid-template-columns: 1fr;
          }
          .demo-modal-body {
            padding: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default DemoRequestModal;